import React, { useEffect, useState } from "react";
import { adminAPI, authAPI, terminalWsUrl } from "../services/api";
import { fmtDateTime } from "../utils/date";
import { Terminal as XTerm } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";

const card = (extra) => ({ background: "#fff", border: "1px solid #e8edf3", borderRadius: 16, boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 10px 30px rgba(15,23,42,0.035)", ...extra });
const btn = (bg, extra) => ({ background: bg, color: "#fff", border: "none", borderRadius: 8, padding: "7px 12px", fontWeight: 700, fontSize: 12.5, cursor: "pointer", ...extra });
const ghost = { background: "#fff", color: "#334155", border: "1px solid #e2e8f0", borderRadius: 8, padding: "7px 12px", fontWeight: 700, fontSize: 12.5, cursor: "pointer" };
const th = { textAlign: "left", padding: "10px 12px", fontSize: 11.5, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: "1px solid #eef1f6", whiteSpace: "nowrap" };
const td = { padding: "10px 12px", fontSize: 13, color: "#0f172a", borderBottom: "1px solid #f3f5f9", verticalAlign: "middle" };
const pill = (bg, c) => ({ display: "inline-block", padding: "2px 9px", borderRadius: 999, fontSize: 11, fontWeight: 700, background: bg, color: c });

export default function AdminPanel() {
  const [tab, setTab] = useState("users");
  const [me, setMe] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    authAPI.me().then((r) => {
      setMe(r.data);
      if (!r.data.isAdmin) setDenied(true);
    }).catch(() => setDenied(true));
  }, []);

  if (denied) {
    return (
      <div style={{ padding: 40, maxWidth: 560, margin: "60px auto", textAlign: "center" }}>
        <div style={card({ padding: 36 })}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>🔒</div>
          <div style={{ fontWeight: 800, fontSize: 18, color: "#0f172a" }}>Admin access required</div>
          <div style={{ color: "#64748b", marginTop: 8, fontSize: 13.5 }}>Yeh page sirf admin account ke liye hai.</div>
        </div>
      </div>
    );
  }
  if (!me) return <div style={{ padding: 40, color: "#64748b" }}>Loading…</div>;

  return (
    <div style={{ padding: "18px 20px 60px" }}>
      <div style={{ background: "linear-gradient(120deg,#4f46e5 0%,#2563eb 55%,#1d4ed8 100%)", borderRadius: 18, padding: "22px 26px", marginBottom: 22, color: "#fff", boxShadow: "0 14px 34px rgba(37,99,235,0.26)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 46, height: 46, borderRadius: 13, background: "rgba(255,255,255,0.16)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 23 }}>🛡️</div>
          <div>
            <div style={{ fontWeight: 900, fontSize: 20 }}>Admin Panel</div>
            <div style={{ opacity: 0.9, fontSize: 13 }}>All users manage karo + naye features ka rollout control karo. Logged in as <b>{me.email}</b>.</div>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {[["users", "👥 Users"], ["flags", "🚩 Feature Rollout"], ["youtube", "📺 YouTube Brain"], ["dynamic", "🧪 Dynamic Builder"], ["aicmd", "🤖 AI Command"], ["terminal", "🖥️ Terminal"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} style={tab === k ? btn("#2563eb") : ghost}>{label}</button>
        ))}
      </div>

      {tab === "users" ? <UsersTab />
        : tab === "flags" ? <FlagsTab adminEmail={me.email} />
        : tab === "youtube" ? <YoutubeTab />
        : tab === "dynamic" ? <DynamicBuilderTab />
        : tab === "aicmd" ? <AiCommandTab />
        : <TerminalTab />}
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");

  const load = () => {
    setErr("");
    adminAPI.listUsers().then((r) => setUsers(r.data)).catch((e) => setErr(e?.response?.data?.message || e.message));
  };
  useEffect(load, []);

  const save = async (u, patch) => {
    setBusy(u._id);
    try { await adminAPI.updateUser(u._id, patch); load(); }
    catch (e) { alert(e?.response?.data?.message || e.message); }
    finally { setBusy(""); }
  };

  // Log in as this user: stash the admin's own token so they can return, swap in
  // the user's token, then hard-reload into the dashboard as that user.
  const loginAs = async (u) => {
    if (!window.confirm(`Login as ${u.email}? Aap is user ke portal me chale jaoge (apna admin session safe rahega).`)) return;
    setBusy(u._id);
    try {
      const r = await adminAPI.loginAs(u._id);
      if (!localStorage.getItem("adminToken")) localStorage.setItem("adminToken", localStorage.getItem("token") || "");
      localStorage.setItem("impersonating", u.email);
      localStorage.setItem("token", r.data.token);
      window.location.href = "/";
    } catch (e) {
      alert(e?.response?.data?.message || e.message);
      setBusy("");
    }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? Yeh permanent hai.`)) return;
    setBusy(u._id);
    try {
      await adminAPI.deleteUser(u._id);
      load();
    } catch (e) {
      const msg = e?.response?.data?.message || e.message;
      // Engine-running guard → offer force delete.
      if (e?.response?.status === 409 && window.confirm(`${msg}\n\nForce delete anyway?`)) {
        try { await adminAPI.deleteUser(u._id, true); load(); }
        catch (e2) { alert(e2?.response?.data?.message || e2.message); }
      } else { alert(msg); }
    } finally { setBusy(""); }
  };

  if (err) return <div style={card({ padding: 18, color: "#ef4444" })}>{err}</div>;
  if (!users) return <div style={{ color: "#64748b" }}>Loading users…</div>;

  return (
    <div style={card({ overflow: "hidden" })}>
      <div style={{ padding: "14px 16px", borderBottom: "1px solid #eef1f6", fontWeight: 800, fontSize: 14 }}>
        Total users: {users.length}
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 920 }}>
          <thead>
            <tr>
              {["User", "Plan", "Admin", "Engine", "Strats", "Brokers", "Created", "Actions"].map((h) => <th key={h} style={th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u._id}>
                <td style={td}>
                  <div style={{ fontWeight: 700 }}>{u.name || "—"}</div>
                  <div style={{ color: "#64748b", fontSize: 12 }}>{u.email}</div>
                  <div style={{ color: "#94a3b8", fontSize: 11 }}>{u.userId}{!u.isActive && <span style={{ ...pill("#fef2f2", "#ef4444"), marginLeft: 6 }}>inactive</span>}</div>
                </td>
                <td style={td}>
                  <select defaultValue={u.plan} onChange={(e) => save(u, { plan: e.target.value })} style={{ padding: "6px 8px", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 12.5 }}>
                    {["free", "unlimited", "pro", "basic"].map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </td>
                <td style={td}>
                  {u.isAdmin
                    ? <span style={pill("#eef2ff", "#4f46e5")}>admin</span>
                    : <span style={pill("#f1f5f9", "#64748b")}>user</span>}
                  <button onClick={() => save(u, { isAdmin: !u.isAdmin })} disabled={busy === u._id} style={{ ...ghost, marginLeft: 8, padding: "4px 8px", fontSize: 11 }}>
                    {u.isAdmin ? "Revoke" : "Make admin"}
                  </button>
                </td>
                <td style={td}>{u.engineRunning ? <span style={pill("#ecfdf5", "#10b981")}>running</span> : <span style={pill("#f1f5f9", "#64748b")}>off</span>}</td>
                <td style={td}>{u.strategies}</td>
                <td style={td}>{u.brokers}</td>
                <td style={{ ...td, color: "#64748b", fontSize: 12, whiteSpace: "nowrap" }}>{u.createdAt ? fmtDateTime(u.createdAt) : "—"}</td>
                <td style={td}>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <button onClick={() => loginAs(u)} disabled={busy === u._id} style={btn("#2563eb", { padding: "5px 10px", fontSize: 11.5 })}>Login as</button>
                    <button onClick={() => del(u)} disabled={busy === u._id} style={btn("#ef4444", { padding: "5px 10px", fontSize: 11.5 })}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const statusPill = (s) => ({
  ok: pill("#ecfdf5", "#10b981"),
  no_transcript: pill("#fff7ed", "#ea580c"),
  not_a_strategy: pill("#f1f5f9", "#64748b"),
  error: pill("#fef2f2", "#ef4444"),
}[s] || pill("#f1f5f9", "#64748b"));

const statusLabel = { ok: "saved", no_transcript: "no transcript", not_a_strategy: "not a strategy", error: "error" };

function YoutubeTab() {
  const [sub, setSub] = useState("selling");      // buying | selling | other
  const [data, setData] = useState(null);          // { items, stats, cookies }
  const [err, setErr] = useState("");
  const [channel, setChannel] = useState("");
  const [max, setMax] = useState(10);
  const [videoUrl, setVideoUrl] = useState("");
  const [busy, setBusy] = useState("");
  const [job, setJob] = useState(null);
  const [showCookies, setShowCookies] = useState(false);
  const [cookieText, setCookieText] = useState("");
  const [expanded, setExpanded] = useState({});
  const [pasteFor, setPasteFor] = useState(null);  // item id awaiting a manual transcript
  const [pasteText, setPasteText] = useState("");
  const [cfg, setCfg] = useState(null);            // daily auto-discovery config
  const [perQ, setPerQ] = useState(6);             // results per topic for a manual discover
  const pollRef = React.useRef(null);

  const load = (s = sub) => {
    setErr("");
    adminAPI.ytList(s).then((r) => setData(r.data)).catch((e) => setErr(e?.response?.data?.message || e.message));
  };
  useEffect(() => { load(sub); /* eslint-disable-next-line */ }, [sub]);
  useEffect(() => () => clearTimeout(pollRef.current), []);
  useEffect(() => { adminAPI.ytGetDiscoveryConfig().then((r) => setCfg(r.data)).catch(() => {}); }, []);

  const runDiscover = async (sources) => {
    setBusy("discover-" + sources.join("")); setJob(null); setErr("");
    try {
      const r = await adminAPI.ytDiscover({ sources, category: sub === "other" ? undefined : sub, perQuery: Number(perQ) });
      poll(r.data.jobId);
    } catch (e) { setErr(e?.response?.data?.message || e.message); setBusy(""); }
  };
  const saveCfg = async (patch) => {
    try { const r = await adminAPI.ytSaveDiscoveryConfig({ ...cfg, ...patch }); setCfg(r.data); }
    catch (e) { alert(e?.response?.data?.message || e.message); }
  };

  const poll = (jobId) => {
    adminAPI.ytJob(jobId).then((r) => {
      setJob(r.data);
      if (r.data.status === "running") {
        pollRef.current = setTimeout(() => poll(jobId), 2000);
      } else {
        setBusy("");
        load();
      }
    }).catch(() => { setBusy(""); });
  };

  const pullChannel = async () => {
    if (!channel.trim()) return;
    setBusy("channel"); setJob(null); setErr("");
    try {
      const r = await adminAPI.ytIngestChannel({ channel: channel.trim(), category: sub === "other" ? undefined : sub, max: Number(max) });
      poll(r.data.jobId);
    } catch (e) { setErr(e?.response?.data?.message || e.message); setBusy(""); }
  };

  const pullVideo = async () => {
    if (!videoUrl.trim()) return;
    setBusy("video"); setErr("");
    try {
      await adminAPI.ytIngestVideo({ url: videoUrl.trim(), category: sub === "other" ? undefined : sub });
      setVideoUrl(""); load();
    } catch (e) { setErr(e?.response?.data?.message || e.message); }
    finally { setBusy(""); }
  };

  const saveCookies = async (clear) => {
    setBusy("cookies");
    try {
      await adminAPI.ytSaveCookies(clear ? "" : cookieText, clear);
      setCookieText(""); setShowCookies(false); load();
    } catch (e) { alert(e?.response?.data?.message || e.message); }
    finally { setBusy(""); }
  };

  const submitPaste = async (id) => {
    setBusy(id);
    try { await adminAPI.ytTranscript(id, pasteText); setPasteFor(null); setPasteText(""); load(); }
    catch (e) { alert(e?.response?.data?.message || e.message); }
    finally { setBusy(""); }
  };
  const move = async (item, category) => { setBusy(item._id); try { await adminAPI.ytUpdate(item._id, { category }); load(); } finally { setBusy(""); } };
  const toggleTrain = async (item) => { setBusy(item._id); try { await adminAPI.ytUpdate(item._id, { useForTraining: !item.useForTraining }); load(); } finally { setBusy(""); } };
  const del = async (item) => { if (!window.confirm(`Delete "${item.title || item.videoId}"?`)) return; setBusy(item._id); try { await adminAPI.ytDelete(item._id); load(); } finally { setBusy(""); } };

  const stats = data?.stats || {};
  const cookies = data?.cookies;
  const items = data?.items || [];

  return (
    <div>
      {/* Intro */}
      <div style={card({ padding: 16, marginBottom: 14 })}>
        <div style={{ fontWeight: 800, fontSize: 14.5, marginBottom: 4 }}>📺 YouTube Brain — strategy knowledge bank</div>
        <div style={{ color: "#64748b", fontSize: 12.8, lineHeight: 1.6 }}>
          Kisi bhi option <b>buying</b> ya <b>selling</b> YouTube channel ka handle/URL daalo. Portal uske videos ki transcript se strategy ka pura logic + summary nikaal kar yahan save karta hai. Yeh saara distilled gyaan <b>"Create a Strategy with a Prompt"</b> ko ground karta hai — jisse user koi bhi complex strategy prompt se zyada accurately bana sake.
        </div>
        <div style={{ display: "flex", gap: 14, marginTop: 12, flexWrap: "wrap" }}>
          {[["selling", "Selling", stats.selling], ["buying", "Buying", stats.buying], ["other", "Other", stats.other]].map(([k, l, n]) => (
            <div key={k} style={{ fontSize: 12.5, color: "#475569" }}><b style={{ fontSize: 15, color: "#0f172a" }}>{n || 0}</b> {l}</div>
          ))}
          <div style={{ fontSize: 12.5, color: "#475569" }}><b style={{ fontSize: 15, color: "#10b981" }}>{stats.trainable || 0}</b> training the prompt builder</div>
        </div>
      </div>

      {/* Cookies status — reliability for transcript fetch */}
      <div style={card({ padding: 14, marginBottom: 14 })}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={cookies ? pill("#ecfdf5", "#10b981") : pill("#fff7ed", "#ea580c")}>{cookies ? "✓ YouTube cookies set" : "⚠ No cookies"}</span>
          <span style={{ color: "#64748b", fontSize: 12.5, flex: 1, minWidth: 220 }}>
            {cookies
              ? "Transcript fetch authenticated — reliable. Agar fir bhi block ho to cookies refresh kar dena."
              : "Server IP se YouTube transcript fetch bot-check me block ho sakta hai. Apne logged-in browser se cookies.txt export karke yahan paste karo (ek baar) — phir reliably chalega."}
          </span>
          <button onClick={() => setShowCookies((v) => !v)} style={ghost}>{showCookies ? "Close" : (cookies ? "Update cookies" : "Setup cookies")}</button>
          {cookies && <button onClick={() => saveCookies(true)} disabled={busy === "cookies"} style={ghost}>Clear</button>}
        </div>
        {showCookies && (
          <div style={{ marginTop: 12 }}>
            <div style={{ color: "#64748b", fontSize: 12, marginBottom: 6 }}>
              Chrome/Firefox me "Get cookies.txt LOCALLY" extension se youtube.com ke cookies export karo, file ka pura text yahan paste karo (Netscape format).
            </div>
            <textarea value={cookieText} onChange={(e) => setCookieText(e.target.value)} placeholder="# Netscape HTTP Cookie File ..." rows={5} style={{ width: "100%", fontFamily: "monospace", fontSize: 11.5, border: "1px solid #e2e8f0", borderRadius: 8, padding: 10 }} />
            <button onClick={() => saveCookies(false)} disabled={busy === "cookies" || cookieText.trim().length < 50} style={{ ...btn("#2563eb"), marginTop: 8 }}>Save cookies</button>
          </div>
        )}
      </div>

      {/* Subtabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {[["selling", "📉 Option Selling"], ["buying", "📈 Option Buying"], ["other", "🗂 Other"]].map(([k, l]) => (
          <button key={k} onClick={() => setSub(k)} style={sub === k ? btn("#7c3aed") : ghost}>{l}</button>
        ))}
      </div>

      {/* Auto-discover — portal finds strategies on its own */}
      <div style={card({ padding: 16, marginBottom: 14, border: "1px solid #ede9fe", background: "linear-gradient(180deg,#faf5ff, #fff)" })}>
        <div style={{ fontWeight: 800, fontSize: 13.5, marginBottom: 2 }}>🔎 Auto-discover {sub === "other" ? "strategies" : `${sub} strategies`}</div>
        <div style={{ color: "#64748b", fontSize: 12.3, marginBottom: 10 }}>
          Portal khud YouTube aur internet (blogs/articles) pe {sub === "other" ? "option" : sub} strategies dhoondega, logic nikaal kar save karega. Koi channel daalne ki zaroorat nahi.
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <button onClick={() => runDiscover(["youtube"])} disabled={!!busy} style={btn("#7c3aed")}>{busy === "discover-youtube" ? "Searching…" : "📺 Discover on YouTube"}</button>
          <button onClick={() => runDiscover(["web"])} disabled={!!busy} style={btn("#0d9488")}>{busy === "discover-web" ? "Searching…" : "🌐 Discover on Web"}</button>
          <button onClick={() => runDiscover(["youtube", "web"])} disabled={!!busy} style={btn("#4f46e5")}>{busy === "discover-youtubeweb" ? "Searching…" : "✨ Discover Both"}</button>
          <span style={{ color: "#64748b", fontSize: 12 }}>
            results/topic
            <select value={perQ} onChange={(e) => setPerQ(e.target.value)} style={{ marginLeft: 6, padding: "5px 8px", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 12.5 }}>
              {[3, 5, 6, 8, 10].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </span>
        </div>
        {/* Daily auto toggle */}
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px dashed #ede9fe", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.8, fontWeight: 700, color: "#0f172a", cursor: "pointer" }}>
            <input type="checkbox" checked={!!(cfg && cfg.enabled)} onChange={(e) => saveCfg({ enabled: e.target.checked })} />
            Daily auto-discovery (07:00 IST)
          </label>
          {cfg && cfg.enabled && (
            <span style={{ display: "flex", gap: 6 }}>
              {["youtube", "web"].map((s) => (
                <label key={s} style={{ fontSize: 12, color: "#475569", display: "flex", alignItems: "center", gap: 4 }}>
                  <input type="checkbox" checked={(cfg.sources || []).includes(s)} onChange={(e) => {
                    const set = new Set(cfg.sources || []); e.target.checked ? set.add(s) : set.delete(s); saveCfg({ sources: [...set] });
                  }} /> {s}
                </label>
              ))}
            </span>
          )}
          {cfg && cfg.lastRunAt && <span style={{ color: "#94a3b8", fontSize: 11.5 }}>last run: {fmtDateTime(cfg.lastRunAt)}</span>}
        </div>
      </div>

      {/* Ingest controls */}
      <div style={card({ padding: 16, marginBottom: 14 })}>
        <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 8 }}>Or pull a specific channel → {sub === "other" ? "auto-classify" : `tag as ${sub}`}</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <input value={channel} onChange={(e) => setChannel(e.target.value)} placeholder="@PRSundar64  or  youtube.com/@channel" style={{ flex: "1 1 280px", padding: "9px 11px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13 }} />
          <select value={max} onChange={(e) => setMax(e.target.value)} style={{ padding: "9px 10px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13 }}>
            {[5, 10, 15, 20, 30].map((n) => <option key={n} value={n}>last {n} videos</option>)}
          </select>
          <button onClick={pullChannel} disabled={busy === "channel"} style={btn("#7c3aed")}>{busy === "channel" ? "Pulling…" : "Pull channel"}</button>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 10, paddingTop: 10, borderTop: "1px dashed #eef1f6" }}>
          <input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="…or one video URL" style={{ flex: "1 1 280px", padding: "9px 11px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13 }} />
          <button onClick={pullVideo} disabled={busy === "video"} style={ghost}>{busy === "video" ? "Working…" : "Add single video"}</button>
        </div>
      </div>

      {/* Live job progress */}
      {job && (
        <div style={card({ padding: 14, marginBottom: 14 })}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ fontWeight: 800, fontSize: 13 }}>
              {job.status === "running" ? "Ingesting…" : job.status === "error" ? "Ingest failed" : "Ingest complete"} {job.total ? `(${job.done}/${job.total})` : ""}
            </div>
            <div style={{ fontSize: 12, color: "#64748b" }}>✓ {job.ok || 0} · ↷ {job.skipped || 0} · ✗ {job.failed || 0}</div>
          </div>
          <div style={{ height: 6, background: "#eef1f6", borderRadius: 999, overflow: "hidden", marginBottom: 8 }}>
            <div style={{ height: "100%", width: `${job.total ? Math.round((job.done / job.total) * 100) : (job.status === "running" ? 8 : 100)}%`, background: job.status === "error" ? "#ef4444" : "#7c3aed", transition: "width .3s" }} />
          </div>
          {job.current && job.status === "running" && <div style={{ fontSize: 12, color: "#475569", marginBottom: 6 }}>▶ {job.current}</div>}
          <div style={{ maxHeight: 150, overflowY: "auto", background: "#0f172a", color: "#cbd5e1", borderRadius: 8, padding: 10, fontFamily: "monospace", fontSize: 11.5, lineHeight: 1.6 }}>
            {(job.log || []).map((l, i) => <div key={i}>{l}</div>)}
          </div>
        </div>
      )}

      {err && <div style={card({ padding: 14, color: "#ef4444", marginBottom: 12 })}>{err}</div>}

      {/* Saved knowledge list */}
      <div style={card({ overflow: "hidden" })}>
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #eef1f6", fontWeight: 800, fontSize: 13.5 }}>
          {sub === "selling" ? "Option Selling" : sub === "buying" ? "Option Buying" : "Other"} — {items.length} saved
        </div>
        {!data ? <div style={{ padding: 16, color: "#64748b" }}>Loading…</div>
          : items.length === 0 ? <div style={{ padding: 18, color: "#64748b", fontSize: 13 }}>Abhi kuch nahi. Upar koi {sub} channel ka handle daal kar "Pull channel" dabao.</div>
            : (
              <div>
                {items.map((it) => (
                  <div key={it._id} style={{ padding: "12px 16px", borderBottom: "1px solid #f3f5f9" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                      <div style={{ flex: 1, minWidth: 240 }}>
                        <a href={it.videoUrl} target="_blank" rel="noreferrer" style={{ fontWeight: 700, color: "#0f172a", textDecoration: "none", fontSize: 13.5 }}>{it.title || it.videoId}</a>
                        <div style={{ color: "#64748b", fontSize: 12, marginTop: 2 }}>
                          <span title={it.source === "web" ? "web article" : "YouTube"}>{it.source === "web" ? "🌐" : "📺"}</span> {it.channelTitle || "—"} · <span style={statusPill(it.status)}>{statusLabel[it.status] || it.status}</span>
                          {it.status === "ok" && (it.useForTraining ? <span style={{ ...pill("#eef2ff", "#4f46e5"), marginLeft: 6 }}>training</span> : <span style={{ ...pill("#f1f5f9", "#94a3b8"), marginLeft: 6 }}>muted</span>)}
                          {it.transcriptSource === "manual" && <span style={{ ...pill("#f0fdfa", "#0d9488"), marginLeft: 6 }}>manual</span>}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "flex-start" }}>
                        {it.summary && <button onClick={() => setExpanded((e) => ({ ...e, [it._id]: !e[it._id] }))} style={{ ...ghost, padding: "5px 9px", fontSize: 11.5 }}>{expanded[it._id] ? "Hide" : "Logic"}</button>}
                        {it.status === "ok" && <button onClick={() => toggleTrain(it)} disabled={busy === it._id} style={{ ...ghost, padding: "5px 9px", fontSize: 11.5 }}>{it.useForTraining ? "Mute" : "Train"}</button>}
                        {sub !== "selling" && <button onClick={() => move(it, "selling")} disabled={busy === it._id} style={{ ...ghost, padding: "5px 9px", fontSize: 11.5 }}>→ Selling</button>}
                        {sub !== "buying" && <button onClick={() => move(it, "buying")} disabled={busy === it._id} style={{ ...ghost, padding: "5px 9px", fontSize: 11.5 }}>→ Buying</button>}
                        {(it.status === "no_transcript" || it.status === "error") && <button onClick={() => { setPasteFor(pasteFor === it._id ? null : it._id); setPasteText(""); }} style={{ ...btn("#0d9488"), padding: "5px 9px", fontSize: 11.5 }}>Paste transcript</button>}
                        <button onClick={() => del(it)} disabled={busy === it._id} style={{ ...btn("#ef4444"), padding: "5px 9px", fontSize: 11.5 }}>Del</button>
                      </div>
                    </div>

                    {it.tags && it.tags.length > 0 && (
                      <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 6 }}>
                        {it.tags.map((t, i) => <span key={i} style={pill("#f8fafc", "#64748b")}>{t}</span>)}
                      </div>
                    )}

                    {expanded[it._id] && (
                      <div style={{ marginTop: 10, background: "#f8fafc", border: "1px solid #eef1f6", borderRadius: 8, padding: 12, fontSize: 12.5, color: "#334155", lineHeight: 1.6 }}>
                        {it.summary && <div style={{ marginBottom: 8 }}>{it.summary}</div>}
                        {it.logic && Object.entries(it.logic).filter(([, v]) => v && (Array.isArray(v) ? v.length : String(v).trim())).map(([k, v]) => (
                          <div key={k} style={{ marginBottom: 3 }}><b style={{ textTransform: "capitalize" }}>{k.replace(/([A-Z])/g, " $1")}:</b> {Array.isArray(v) ? v.join("; ") : String(v)}</div>
                        ))}
                      </div>
                    )}

                    {pasteFor === it._id && (
                      <div style={{ marginTop: 10 }}>
                        <textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)} placeholder="Video ki transcript yahan paste karo…" rows={4} style={{ width: "100%", border: "1px solid #e2e8f0", borderRadius: 8, padding: 10, fontSize: 12.5 }} />
                        <button onClick={() => submitPaste(it._id)} disabled={busy === it._id || pasteText.trim().length < 80} style={{ ...btn("#0d9488"), marginTop: 6 }}>Extract from transcript</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
      </div>
    </div>
  );
}

const inr = (n) => "₹" + Math.round(Number(n) || 0).toLocaleString("en-IN");
function isoDaysAgo(d) { const x = new Date(Date.now() - d * 864e5); return x.toISOString().slice(0, 10); }

function DynamicBuilderTab() {
  const [cat, setCat] = useState("");           // "" = all | buying | selling
  const [limitArch, setLimitArch] = useState(15);
  const [maxVar, setMaxVar] = useState(10);
  const [from, setFrom] = useState(isoDaysAgo(182));
  const [to, setTo] = useState(isoDaysAgo(1));
  const [sortBy, setSortBy] = useState("score");
  const [board, setBoard] = useState(null);     // { items, stats }
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [detail, setDetail] = useState(null);
  const pollRef = React.useRef(null);

  const load = () => {
    adminAPI.dynLeaderboard(cat || undefined, sortBy, 80).then((r) => setBoard(r.data)).catch((e) => setErr(e?.response?.data?.message || e.message));
  };
  useEffect(load, [cat, sortBy]); // eslint-disable-line
  useEffect(() => () => clearTimeout(pollRef.current), []);

  const poll = (id) => {
    adminAPI.dynJob(id).then((r) => {
      setJob(r.data);
      if (r.data.status === "running") pollRef.current = setTimeout(() => poll(id), 2500);
      else { setBusy(""); load(); }
    }).catch(() => setBusy(""));
  };

  const run = async () => {
    setBusy("run"); setJob(null); setErr("");
    try {
      const r = await adminAPI.dynRun({ category: cat || undefined, limitArchetypes: Number(limitArch), maxVariants: Number(maxVar), from, to });
      poll(r.data.jobId);
    } catch (e) { setErr(e?.response?.data?.message || e.message); setBusy(""); }
  };
  const view = async (id) => { try { const r = await adminAPI.dynGet(id); setDetail(r.data); } catch (e) { alert(e?.response?.data?.message || e.message); } };
  const saveStrat = async (id) => { setBusy(id); try { const r = await adminAPI.dynSaveStrategy(id); alert(`Saved as strategy "${r.data.name}". Ab Strategies page se load/deploy kar sakte ho.`); } catch (e) { alert(e?.response?.data?.message || e.message); } finally { setBusy(""); } };
  const del = async (id) => { if (!window.confirm("Delete this candidate?")) return; setBusy(id); try { await adminAPI.dynDelete(id); load(); } finally { setBusy(""); } };

  const stats = board?.stats || {};
  const items = board?.items || [];

  return (
    <div>
      <div style={card({ padding: 16, marginBottom: 14 })}>
        <div style={{ fontWeight: 800, fontSize: 14.5, marginBottom: 4 }}>🧪 Dynamic Strategy Builder</div>
        <div style={{ color: "#64748b", fontSize: 12.8, lineHeight: 1.6 }}>
          YouTube Brain ki har distilled strategy ko portal ek real strategy me convert karta hai, uske <b>parameter variants</b> (entry time × SL × target) banata hai, sabko <b>real data pe backtest</b> karta hai, aur best ko rank karta hai. Jo jeet-te hain unka proven config wapas knowledge me likha jaata hai → <b>"Create a Strategy with a Prompt"</b> aur accurate ho jaata hai.
        </div>
        <div style={{ display: "flex", gap: 14, marginTop: 12, flexWrap: "wrap" }}>
          <div style={{ fontSize: 12.5, color: "#475569" }}><b style={{ fontSize: 15, color: "#0f172a" }}>{stats.total || 0}</b> candidates</div>
          <div style={{ fontSize: 12.5, color: "#475569" }}><b style={{ fontSize: 15, color: "#2563eb" }}>{stats.backtested || 0}</b> backtested</div>
          <div style={{ fontSize: 12.5, color: "#475569" }}><b style={{ fontSize: 15, color: "#10b981" }}>{stats.profitable || 0}</b> profitable</div>
        </div>
      </div>

      {/* Run controls */}
      <div style={card({ padding: 16, marginBottom: 14 })}>
        <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 10 }}>Build + backtest a batch</div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <Field label="Category"><select value={cat} onChange={(e) => setCat(e.target.value)} style={sel}><option value="">All</option><option value="selling">Selling</option><option value="buying">Buying</option></select></Field>
          <Field label="Archetypes"><select value={limitArch} onChange={(e) => setLimitArch(e.target.value)} style={sel}>{[5, 10, 15, 25, 40, 60].map((n) => <option key={n} value={n}>{n}</option>)}</select></Field>
          <Field label="Variants/each"><select value={maxVar} onChange={(e) => setMaxVar(e.target.value)} style={sel}>{[1, 5, 10, 16, 24].map((n) => <option key={n} value={n}>{n}</option>)}</select></Field>
          <Field label="From"><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={sel} /></Field>
          <Field label="To"><input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={sel} /></Field>
          <button onClick={run} disabled={busy === "run"} style={btn("#2563eb")}>{busy === "run" ? "Running…" : "▶ Build & Backtest"}</button>
        </div>
        <div style={{ color: "#94a3b8", fontSize: 11.5, marginTop: 8 }}>
          ~{limitArch} × {Number(maxVar) + 1} = <b>{limitArch * (Number(maxVar) + 1)}</b> backtests. Data Oct-2024 se available. Already-built archetypes skip ho jaate hain.
        </div>
      </div>

      {job && (
        <div style={card({ padding: 14, marginBottom: 14 })}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ fontWeight: 800, fontSize: 13 }}>{job.status === "running" ? `Working (${job.phase})…` : job.status === "error" ? "Failed" : "Complete"} {job.total ? `· ${job.done}/${job.total} archetypes` : ""}</div>
            <div style={{ fontSize: 12, color: "#64748b" }}>built {job.built || 0} · backtested {job.backtested || 0} · fail {job.failed || 0}</div>
          </div>
          <div style={{ height: 6, background: "#eef1f6", borderRadius: 999, overflow: "hidden", marginBottom: 8 }}>
            <div style={{ height: "100%", width: `${job.total ? Math.round((job.done / job.total) * 100) : (job.status === "running" ? 8 : 100)}%`, background: job.status === "error" ? "#ef4444" : "#2563eb", transition: "width .3s" }} />
          </div>
          {job.current && job.status === "running" && <div style={{ fontSize: 12, color: "#475569", marginBottom: 6 }}>▶ {job.current}</div>}
          <div style={{ maxHeight: 150, overflowY: "auto", background: "#0f172a", color: "#cbd5e1", borderRadius: 8, padding: 10, fontFamily: "monospace", fontSize: 11.5, lineHeight: 1.6 }}>
            {(job.log || []).map((l, i) => <div key={i}>{l}</div>)}
          </div>
        </div>
      )}

      {err && <div style={card({ padding: 14, color: "#ef4444", marginBottom: 12 })}>{err}</div>}

      {/* Leaderboard */}
      <div style={card({ overflow: "hidden" })}>
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #eef1f6", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div style={{ fontWeight: 800, fontSize: 13.5 }}>🏆 Leaderboard {cat ? `· ${cat}` : ""}</div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <span style={{ fontSize: 11.5, color: "#64748b" }}>sort</span>
            {[["score", "Score"], ["pnl", "Net P&L"], ["winRate", "Win %"]].map(([k, l]) => (
              <button key={k} onClick={() => setSortBy(k)} style={sortBy === k ? btn("#2563eb", { padding: "4px 9px", fontSize: 11.5 }) : { ...ghost, padding: "4px 9px", fontSize: 11.5 }}>{l}</button>
            ))}
          </div>
        </div>
        {!board ? <div style={{ padding: 16, color: "#64748b" }}>Loading…</div>
          : items.length === 0 ? <div style={{ padding: 18, color: "#64748b", fontSize: 13 }}>Abhi koi backtested candidate nahi. Upar se ek batch "Build & Backtest" karo (pehle YouTube Brain me kuch strategies honi chahiye).</div>
            : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 880 }}>
                  <thead><tr>{["#", "Strategy", "Cat", "Config", "Net P&L", "Win %", "Trades", "Max DD", "Score", ""].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {items.map((c, i) => (
                      <tr key={c._id}>
                        <td style={{ ...td, color: "#94a3b8" }}>{i + 1}</td>
                        <td style={td}><div style={{ fontWeight: 700, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</div></td>
                        <td style={td}><span style={pill(c.category === "selling" ? "#fef2f2" : c.category === "buying" ? "#ecfdf5" : "#f1f5f9", c.category === "selling" ? "#ef4444" : c.category === "buying" ? "#10b981" : "#64748b")}>{c.category}</span></td>
                        <td style={{ ...td, fontSize: 11.5, color: "#475569", whiteSpace: "nowrap" }}>{c.isBase ? "as-described" : `${c.params?.entryTime} · SL${c.params?.slPct} · T${c.params?.tgtPct}`}</td>
                        <td style={{ ...td, fontWeight: 800, color: (c.backtest?.pnl || 0) >= 0 ? "#10b981" : "#ef4444", whiteSpace: "nowrap" }}>{inr(c.backtest?.pnl)}</td>
                        <td style={td}>{c.backtest?.winRate}%</td>
                        <td style={td}>{c.backtest?.trades}</td>
                        <td style={{ ...td, color: "#ef4444", whiteSpace: "nowrap" }}>{inr(c.backtest?.maxDrawdown)}</td>
                        <td style={{ ...td, fontWeight: 700 }}>{c.score}</td>
                        <td style={td}>
                          <div style={{ display: "flex", gap: 5 }}>
                            <button onClick={() => view(c._id)} style={{ ...ghost, padding: "4px 8px", fontSize: 11 }}>View</button>
                            <button onClick={() => saveStrat(c._id)} disabled={busy === c._id} style={{ ...btn("#10b981"), padding: "4px 8px", fontSize: 11 }}>Save</button>
                            <button onClick={() => del(c._id)} disabled={busy === c._id} style={{ ...btn("#ef4444"), padding: "4px 8px", fontSize: 11 }}>Del</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </div>

      {detail && <CandidateDetail c={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

const sel = { padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12.5 };
function Field({ label, children }) {
  return <div style={{ display: "flex", flexDirection: "column", gap: 4 }}><span style={{ fontSize: 11, color: "#64748b", fontWeight: 700 }}>{label}</span>{children}</div>;
}

function CandidateDetail({ c, onClose }) {
  const d = c.strategyDoc || {};
  const bt = c.backtest || {};
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={card({ padding: 0, maxWidth: 680, width: "100%", maxHeight: "86vh", overflow: "auto" })}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #eef1f6", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: 800, fontSize: 14 }}>{c.name}</div>
          <button onClick={onClose} style={ghost}>Close</button>
        </div>
        <div style={{ padding: 18, fontSize: 13, color: "#334155", lineHeight: 1.6 }}>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 14 }}>
            <Stat label="Net P&L" value={inr(bt.pnl)} color={(bt.pnl || 0) >= 0 ? "#10b981" : "#ef4444"} />
            <Stat label="Win rate" value={(bt.winRate || 0) + "%"} />
            <Stat label="Trades" value={bt.trades || 0} />
            <Stat label="Max DD" value={inr(bt.maxDrawdown)} color="#ef4444" />
            <Stat label="Sharpe" value={bt.sharpe || 0} />
          </div>
          <div style={{ color: "#64748b", fontSize: 12, marginBottom: 12 }}>{bt.dataMode} · {bt.from} → {bt.to}</div>
          {c.coverage === "partial" && c.unsupported?.length > 0 && (
            <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 8, padding: 10, marginBottom: 12, fontSize: 12 }}>
              <b>Partial mapping</b> — couldn't express: {c.unsupported.join("; ")}
            </div>
          )}
          <div style={{ fontWeight: 800, fontSize: 12.5, marginBottom: 6 }}>Strategy config</div>
          <div style={{ background: "#f8fafc", border: "1px solid #eef1f6", borderRadius: 8, padding: 12, fontSize: 12.5 }}>
            <div><b>Instrument:</b> {d.instrument} · <b>Entry:</b> {d.entryTime} · <b>Exit:</b> {d.exitTime} · <b>Product:</b> {d.productType || d.orderType}</div>
            <div style={{ marginTop: 6 }}><b>Legs:</b></div>
            {(d.legs || []).map((l, i) => (
              <div key={i} style={{ marginLeft: 10 }}>• {(l.action || l.buySell || "").toUpperCase()} {l.callPut || l.optionType} {l.strikeType || l.strikeBy} — SL {l.slValue}{l.slType === "percent" ? "%" : ""} / TGT {l.targetValue}{l.tpType === "percent" ? "%" : ""}</div>
            ))}
          </div>
          {c.knowledgeId && (
            <div style={{ marginTop: 12, fontSize: 12, color: "#64748b" }}>
              Source: {c.knowledgeId.source === "web" ? "🌐" : "📺"} <a href={c.knowledgeId.videoUrl} target="_blank" rel="noreferrer" style={{ color: "#2563eb" }}>{c.knowledgeId.title}</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
// Turn the raw Anthropic message array (session.messages — incl. tool_use /
// tool_result blocks) into an ordered list of renderable turns. tool_use
// blocks arrive on an assistant message before the matching tool_result (on
// the following user message), so we track tool_use_id → {name,input} as we
// walk forward and pair them up.
function flattenAiCmdTranscript(messages) {
  const toolMeta = {};
  const turns = [];
  for (const m of messages || []) {
    if (m.role === "user") {
      if (typeof m.content === "string") turns.push({ type: "user_prompt", text: m.content });
      else if (Array.isArray(m.content)) {
        for (const block of m.content) {
          if (block.type === "tool_result") {
            const meta = toolMeta[block.tool_use_id] || {};
            turns.push({ type: "tool_call", name: meta.name || "?", input: meta.input, result: block.content });
          }
        }
      }
    } else if (m.role === "assistant" && Array.isArray(m.content)) {
      for (const block of m.content) {
        if (block.type === "text" && block.text) turns.push({ type: "assistant_text", text: block.text });
        else if (block.type === "tool_use") toolMeta[block.id] = { name: block.name, input: block.input };
      }
    }
  }
  return turns;
}

function AiCommandTab() {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [session, setSession] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [err, setErr] = useState("");
  const [expanded, setExpanded] = useState({});
  const pollRef = React.useRef(null);
  const bottomRef = React.useRef(null);

  const loadSessions = (selectFirst) => {
    adminAPI.aiCmdSessions().then((r) => {
      setSessions(r.data);
      if (selectFirst && r.data.length) selectSession(r.data[0]._id);
    }).catch((e) => setErr(e?.response?.data?.message || e.message));
  };
  useEffect(() => { loadSessions(true); /* eslint-disable-line */ }, []);
  useEffect(() => () => clearTimeout(pollRef.current), []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [session, job]);

  const selectSession = (id) => {
    setSessionId(id);
    adminAPI.aiCmdSession(id).then((r) => setSession(r.data)).catch((e) => setErr(e?.response?.data?.message || e.message));
  };

  const newSession = async () => {
    setErr(""); setJob(null);
    try {
      const r = await adminAPI.aiCmdNewSession();
      setSession({ _id: r.data.sessionId, title: "", messages: [] });
      setSessionId(r.data.sessionId);
      loadSessions(false);
    } catch (e) { setErr(e?.response?.data?.message || e.message); }
  };

  const poll = (id) => {
    adminAPI.aiCmdJob(id).then((r) => {
      setJob(r.data);
      if (r.data.status === "running") {
        pollRef.current = setTimeout(() => poll(id), 2000);
      } else {
        setBusy(false);
        selectSession(sessionId);
        loadSessions(false);
      }
    }).catch(() => setBusy(false));
  };

  const send = async () => {
    const text = prompt.trim();
    if (!text || busy) return;
    setErr(""); setJob(null); setBusy(true);
    try {
      let sid = sessionId;
      if (!sid) {
        const r = await adminAPI.aiCmdNewSession();
        sid = r.data.sessionId;
        setSessionId(sid);
      }
      setPrompt("");
      const r = await adminAPI.aiCmdRun(sid, text);
      poll(r.data.jobId);
    } catch (e) { setErr(e?.response?.data?.message || e.message); setBusy(false); }
  };

  const turns = flattenAiCmdTranscript(session?.messages);

  return (
    <div>
      <div style={card({ padding: 16, marginBottom: 14, border: "1px solid #fecaca", background: "linear-gradient(180deg,#fff1f2, #fff)" })}>
        <div style={{ fontWeight: 800, fontSize: 13.5, marginBottom: 4, color: "#991b1b" }}>⚠️ Direct production access — no approval step</div>
        <div style={{ color: "#7f1d1d", fontSize: 12.3, lineHeight: 1.6 }}>
          Ye agent seedha is live trading server par files padh/likh sakta hai aur shell commands (git, npm, pm2 restart samet) run kar sakta hai — koi diff-review nahi hota, jo bhi decide kare turant apply ho jaata hai. Har prompt, har tool call, aur uska result neeche transcript me permanently log hota hai (audit trail).
        </div>
      </div>

      <div style={{ display: "flex", gap: 14 }}>
        {/* Session list */}
        <div style={card({ padding: 10, width: 200, flexShrink: 0, height: 560, overflowY: "auto" })}>
          <button onClick={newSession} style={{ ...btn("#2563eb"), width: "100%", marginBottom: 8 }}>+ New session</button>
          {sessions.map((s) => (
            <div key={s._id} onClick={() => selectSession(s._id)}
              style={{ padding: "8px 9px", borderRadius: 8, marginBottom: 4, cursor: "pointer", fontSize: 12, background: s._id === sessionId ? "#eef2ff" : "transparent", color: s._id === sessionId ? "#4338ca" : "#334155", fontWeight: s._id === sessionId ? 700 : 500 }}>
              <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.title || "(untitled)"}</div>
              <div style={{ color: "#94a3b8", fontSize: 10.5 }}>{fmtDateTime(s.updatedAt)}</div>
            </div>
          ))}
          {sessions.length === 0 && <div style={{ color: "#94a3b8", fontSize: 12, padding: 6 }}>Koi session nahi. Neeche se pehla message bhejo.</div>}
        </div>

        {/* Transcript + composer */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={card({ padding: 16, height: 460, overflowY: "auto", marginBottom: 10 })}>
            {turns.length === 0 && <div style={{ color: "#94a3b8", fontSize: 13 }}>Koi instruction likho — jaise "server pe kitni RAM free hai check karo" ya "engine/StrategyRunner.js me X function dikhao".</div>}
            {turns.map((t, i) => t.type === "user_prompt" ? (
              <div key={i} style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                <div style={{ background: "#2563eb", color: "#fff", padding: "8px 12px", borderRadius: "12px 12px 2px 12px", maxWidth: "80%", fontSize: 13, whiteSpace: "pre-wrap" }}>{t.text}</div>
              </div>
            ) : t.type === "assistant_text" ? (
              <div key={i} style={{ display: "flex", justifyContent: "flex-start", marginBottom: 10 }}>
                <div style={{ background: "#f1f5f9", color: "#0f172a", padding: "8px 12px", borderRadius: "12px 12px 12px 2px", maxWidth: "80%", fontSize: 13, whiteSpace: "pre-wrap" }}>{t.text}</div>
              </div>
            ) : (
              <div key={i} style={{ marginBottom: 8 }}>
                <div onClick={() => setExpanded((e) => ({ ...e, [i]: !e[i] }))} style={{ cursor: "pointer", fontFamily: "monospace", fontSize: 11.5, color: "#7c3aed", background: "#faf5ff", border: "1px solid #ede9fe", borderRadius: 7, padding: "5px 9px", display: "inline-block" }}>
                  🔧 {t.name}({(t.input ? JSON.stringify(t.input) : "").slice(0, 70)}{t.input && JSON.stringify(t.input).length > 70 ? "…" : ""}) {expanded[i] ? "▲" : "▼"}
                </div>
                {expanded[i] && (
                  <pre style={{ background: "#0f172a", color: "#cbd5e1", borderRadius: 8, padding: 10, fontSize: 11, marginTop: 4, overflowX: "auto", maxHeight: 220 }}>
                    {typeof t.result === "string" ? t.result : JSON.stringify(t.result, null, 2)}
                  </pre>
                )}
              </div>
            ))}
            {job && job.status === "running" && (
              <div style={{ color: "#7c3aed", fontSize: 12, fontFamily: "monospace" }}>▶ {job.log[job.log.length - 1] || "working…"}</div>
            )}
            {job && job.status === "error" && (
              <div style={{ color: "#ef4444", fontSize: 12.5, marginTop: 6 }}>✗ {job.log[job.log.length - 1] || "Failed."}</div>
            )}
            <div ref={bottomRef} />
          </div>

          {err && <div style={{ color: "#ef4444", fontSize: 12.5, marginBottom: 8 }}>{err}</div>}

          <div style={{ display: "flex", gap: 8 }}>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Instruction likho… (Enter = send, Shift+Enter = new line)" rows={2}
              style={{ flex: 1, padding: "9px 11px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, resize: "vertical" }} disabled={busy} />
            <button onClick={send} disabled={busy || !prompt.trim()} style={btn("#2563eb", { alignSelf: "flex-end" })}>{busy ? "Running…" : "Send"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TerminalTab() {
  const containerRef = React.useRef(null);
  const [status, setStatus] = useState("connecting"); // connecting | ready | closed | error
  const [sessions, setSessions] = useState([]);

  const loadSessions = () => adminAPI.termSessions().then((r) => setSessions(r.data)).catch(() => {});
  useEffect(() => { loadSessions(); }, []);

  useEffect(() => {
    const term = new XTerm({
      cursorBlink: true, fontSize: 13, fontFamily: "Menlo, Consolas, monospace",
      theme: { background: "#0f172a", foreground: "#e2e8f0" },
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(containerRef.current);
    fit.fit();

    const ws = new WebSocket(terminalWsUrl());
    ws.onopen = () => {
      const token = localStorage.getItem("token");
      ws.send(JSON.stringify({ type: "auth", token, cols: term.cols, rows: term.rows }));
    };
    ws.onmessage = (ev) => {
      let msg;
      try { msg = JSON.parse(ev.data); } catch { return; }
      if (msg.type === "ready") setStatus("ready");
      else if (msg.type === "data") term.write(msg.data);
    };
    ws.onclose = () => setStatus("closed");
    ws.onerror = () => setStatus("error");

    term.onData((data) => { if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "input", data })); });

    const resizeObserver = new ResizeObserver(() => {
      fit.fit();
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "resize", cols: term.cols, rows: term.rows }));
    });
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      ws.close();
      term.dispose();
      loadSessions();
    };
    // eslint-disable-next-line
  }, []);

  return (
    <div>
      <div style={card({ padding: 16, marginBottom: 14, border: "1px solid #fecaca", background: "linear-gradient(180deg,#fff1f2, #fff)" })}>
        <div style={{ fontWeight: 800, fontSize: 13.5, marginBottom: 4, color: "#991b1b" }}>⚠️ Real shell — production server, no AI/API</div>
        <div style={{ color: "#7f1d1d", fontSize: 12.3, lineHeight: 1.6 }}>
          Ye is live trading server ka asli terminal hai — jo bhi command chalega turant apply hoga. Har typed command neeche "Recent sessions" me audit ke liye record hoti hai.
        </div>
      </div>

      <div style={{ ...card({ padding: 0, overflow: "hidden" }), marginBottom: 14 }}>
        <div style={{ padding: "8px 12px", borderBottom: "1px solid #1e293b", background: "#0f172a", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ color: "#94a3b8", fontSize: 11.5, fontFamily: "monospace" }}>bash</span>
          <span style={pill(status === "ready" ? "#052e1f" : "#3f1d1d", status === "ready" ? "#34d399" : "#f87171")}>{status}</span>
        </div>
        <div ref={containerRef} style={{ height: 460, background: "#0f172a", padding: 6 }} />
      </div>

      <div style={card({ padding: 14 })}>
        <div style={{ fontWeight: 800, fontSize: 12.5, marginBottom: 8 }}>Recent sessions (audit)</div>
        {sessions.length === 0 ? <div style={{ color: "#94a3b8", fontSize: 12 }}>Abhi koi session log nahi.</div> : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 480 }}>
              <thead><tr>{["Admin", "Started", "Ended", "Commands"].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s._id}>
                    <td style={td}>{s.user?.email || "—"}</td>
                    <td style={{ ...td, fontSize: 11.5 }}>{fmtDateTime(s.startedAt)}</td>
                    <td style={{ ...td, fontSize: 11.5 }}>{s.endedAt ? fmtDateTime(s.endedAt) : <span style={pill("#ecfdf5", "#10b981")}>live</span>}</td>
                    <td style={td}>{s.commandCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return <div><div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700 }}>{label}</div><div style={{ fontSize: 17, fontWeight: 800, color: color || "#0f172a" }}>{value}</div></div>;
}

function FlagsTab({ adminEmail }) {
  const [flags, setFlags] = useState(null);
  const [err, setErr] = useState("");
  const [key, setKey] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState("");

  const load = () => { setErr(""); adminAPI.listFlags().then((r) => setFlags(r.data)).catch((e) => setErr(e?.response?.data?.message || e.message)); };
  useEffect(load, []);

  const create = async () => {
    if (!key.trim()) return;
    setBusy("new");
    try { await adminAPI.createFlag({ key: key.trim(), label: label.trim() }); setKey(""); setLabel(""); load(); }
    catch (e) { alert(e?.response?.data?.message || e.message); }
    finally { setBusy(""); }
  };
  const toggle = async (f) => {
    setBusy(f.key);
    try { await (f.enabledForAll ? adminAPI.rollback(f.key) : adminAPI.applyAll(f.key)); load(); }
    catch (e) { alert(e?.response?.data?.message || e.message); }
    finally { setBusy(""); }
  };

  return (
    <div>
      <div style={card({ padding: 16, marginBottom: 16 })}>
        <div style={{ fontWeight: 800, fontSize: 13.5, marginBottom: 6 }}>Naya feature flag register karo</div>
        <div style={{ color: "#64748b", fontSize: 12.5, marginBottom: 12 }}>
          Naya flag bante hi <b>sirf admin account</b> me active hota hai. <b>"Apply to all"</b> dabane par hi sab users ko milta hai — tab tak baaki users (running engine samet) purane logic par hi rahenge.
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input value={key} onChange={(e) => setKey(e.target.value)} placeholder="flag key (e.g. roll_entry_guard)" style={{ flex: "1 1 240px", padding: "9px 11px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13 }} />
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="description (optional)" style={{ flex: "2 1 320px", padding: "9px 11px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13 }} />
          <button onClick={create} disabled={busy === "new"} style={btn("#2563eb")}>+ Add flag</button>
        </div>
      </div>

      {err && <div style={card({ padding: 14, color: "#ef4444", marginBottom: 12 })}>{err}</div>}

      <div style={card({ overflow: "hidden" })}>
        <div style={{ padding: "14px 16px", borderBottom: "1px solid #eef1f6", fontWeight: 800, fontSize: 14 }}>Feature flags</div>
        {!flags ? <div style={{ padding: 16, color: "#64748b" }}>Loading…</div>
          : flags.length === 0 ? <div style={{ padding: 16, color: "#64748b", fontSize: 13 }}>Abhi koi flag nahi. Aage se jab naya logic change hoga, wo yahan flag ke roop me aayega.</div>
            : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
                  <thead><tr>{["Key", "Description", "Status", "Last changed", "Action"].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {flags.map((f) => (
                      <tr key={f.key}>
                        <td style={{ ...td, fontFamily: "monospace", fontWeight: 700 }}>{f.key}</td>
                        <td style={{ ...td, color: "#475569" }}>{f.label || "—"}</td>
                        <td style={td}>{f.enabledForAll ? <span style={pill("#ecfdf5", "#10b981")}>all users</span> : <span style={pill("#eef2ff", "#4f46e5")}>admin only</span>}</td>
                        <td style={{ ...td, color: "#64748b", fontSize: 12 }}>{f.updatedAt ? fmtDateTime(f.updatedAt) : "—"}{f.updatedBy ? ` · ${f.updatedBy}` : ""}</td>
                        <td style={td}>
                          <button onClick={() => toggle(f)} disabled={busy === f.key} style={f.enabledForAll ? ghost : btn("#10b981")}>
                            {f.enabledForAll ? "Rollback to admin-only" : "Apply to all"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </div>
    </div>
  );
}
